/* @flow */

import React from 'react';
import { Redirect } from 'react-router-dom';
import to from 'to-case';

import AssetDetailsPage from 'views/AssetDetailsPage/AssetDetailsPage';
import { allRoles } from 'permissions/general';
import { createSingleOwnedAsset } from 'permissions/asset';
import { formulateUri } from 'tables/components/ReportView/state';
import AssetsReportView from 'tables/AssetsReportView/AssetsReportView';

import MultipleAssetForm from '../forms/AssetForm/MultipleAssetForm/MultipleAssetForm';
import SingleAssetForm from '../forms/AssetForm/SingleAssetForm/SingleAssetForm';
import { filterTradingAccountItem } from './utils/filters';

export const formatAssetTypeParam = (assetTypeParam: string) =>
  assetTypeParam
    .split('|')
    .filter(type => typeof type === 'string')
    .map(type => (type === 'rented' ? 'Rented Open' : to.title(type)));

const assetRoutes: Array<*> = [
  {
    path: '/assets',
    title: 'general:document_titles.assets.title',
    component: AssetsReportView,
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/create',
    title: 'general:document_titles.assets.create',
    component: function FormCreateNewSingleAsset(props: *) {
      return <SingleAssetForm params={props.match.params} />;
    },
    exact: true,
    permissions: createSingleOwnedAsset,
  },
  {
    path: '/assets/create-multiple',
    title: 'general:document_titles.assets.create',
    component: function FormCreateNewMultipleAsset(props: *) {
      return <MultipleAssetForm params={props.match.params} />;
    },
    exact: true,
    permissions: createSingleOwnedAsset,
  },
  {
    path: '/assets/edit',
    title: 'general:document_titles.assets.edit',
    component({ location }: Object) {
      return <SingleAssetForm mode="edit" assetData={location.state ? location.state : {}} />;
    },
    exact: true,
    permissions: createSingleOwnedAsset,
  },
  {
    path: '/assets/:id',
    title: 'general:document_titles.assets.detail',
    title_keys: ['asset.asset_number', 'asset.manufacturer', 'asset.model'],
    component: AssetDetailsPage,
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/:id/:actionType',
    title: 'general:document_titles.assets.action_type',
    title_keys: ['asset.asset_number', 'asset.manufacturer', 'asset.model', 'currentActionDrawer'],
    component: AssetDetailsPage,
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/user/:userId',
    component: function AssetsByUser(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [{ key: 'user', values: [props.match.params.userId] }],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/user/:userId/end-date/:dateRange',
    component: function AssetsByUserAndDate(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'user', values: [props.match.params.userId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
              { key: 'calibration_due_date', values: [props.match.params.dateRange] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/cost-center/:costCenterId',
    component: function AssetsByCostCenter(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'cost_centre', values: [props.match.params.costCenterId] },
              { key: 'asset_type', values: ['open_rented', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/calibration-due-date/:dateRange',
    component: function Calibrations(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [{ key: 'calibration_due_date', values: [props.match.params.dateRange] }],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/calibration-due-date/:dateRange',
    component: function Calibrations(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'calibration_due_date', values: [props.match.params.dateRange] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/asset-type/:assetType',
    component: function AssetsByTypeBar(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              {
                key: 'asset_type',
                values: [...formatAssetTypeParam(props.match.params.assetType)],
              },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/asset-status/:assetStatus',
    component: function AssetStatus(props: *) {
      const assetStatusParam: string = encodeURIComponent(
        `asset_status:${props.match.params.assetStatus}`
      );
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'asset_status', values: [...props.match.params.assetStatus.split('|')] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/asset-status/:assetStatus',
    component: function AssetStatus(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'asset_status', values: [props.match.params.assetStatus] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/asset-type/owned/current-condition/:currentCondition',
    component: function AssetQuality(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'asset_type', values: ['Owned'] },
              { key: 'current_condition', values: [props.match.params.currentCondition] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/:assetType/cost-center/:costCenterId',
    component: function AssetsByTypeCostCenter(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'cost_centre', values: [props.match.params.costCenterId] },
              {
                key: 'asset_type',
                values: [...formatAssetTypeParam(props.match.params.assetType)],
              },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/contract/:contractId',
    component: function AssetsByContract(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [{ key: 'contract_id', values: [props.match.params.contractId] }],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/contract/:contractId/model/:model',
    component: function AssetsByContract(props: *) {
      const params = props.match.params;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'contract_id', values: [params.contractId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
              { key: 'model', values: [params.model] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/product/:productId',
    component: function AssetsByProduct(props: *) {
      const params = props.match.params;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'model', values: [params.productId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/product/:productId',
    component: function AssetsByProduct(props: *) {
      const params = props.match.params;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({ key: 'account', values: [params.accountId] }),
              { key: 'model', values: [params.productId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/product/:productId/asset-type/:assetType',
    component: function AssetsByContract(props: *) {
      const params = props.match.params;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'model', values: [params.productId] },
              { key: 'asset_type', values: [...formatAssetTypeParam(params.assetType)] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/product/:productId/asset-type/:assetType',
    component: function AssetsByContract(props: *) {
      const params = props.match.params;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({ key: 'account', values: [params.accountId] }),
              { key: 'model', values: [params.productId] },
              { key: 'asset_type', values: [...formatAssetTypeParam(params.assetType)] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/manufacturer/:manufacturerId',
    component: function AssetsByManufacturer(props: *) {
      const params = props.match.params;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'manufacturer', values: [params.manufacturerId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId',
    component: function AssetsByAccount(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/orphaned',
    component: function AssetsByAccount(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'asset_number', values: [props.location.state] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/asset-type/:assetType/',
    component: function AssetsByAssetType(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              {
                key: 'asset_type',
                values: [...formatAssetTypeParam(props.match.params.assetType)],
              },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/project/:projectId',
    component: function AssetsByProject(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'project', values: [...props.match.params.projectId.split('|')] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/project/:projectId/user/:userId',
    component: function AssetsByProject(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'project', values: [props.match.params.projectId] },
              { key: 'user', values: [props.match.params.userId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/:assetType/project/:projectId',
    component: function AssetsByTypeAndProject(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'project', values: [props.match.params.projectId] },
              {
                key: 'asset_type',
                values: [...formatAssetTypeParam(props.match.params.assetType)],
              },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/:assetType/project/:projectId',
    component: function AssetsByTypeAndProject(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'project', values: [...props.match.params.projectId.split('|')] },
              {
                key: 'asset_type',
                values: [...formatAssetTypeParam(props.match.params.assetType)],
              },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/user/:userId',
    component: function AssetsByAccountAndUser(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'user', values: [props.match.params.userId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/company/:companyId',
    component: function AssetsByCompany(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'company_id', values: [props.match.params.companyId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/company/:companyId/country/:countryId',
    component: function AssetsByCompanyAndCountry(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'company_id', values: [props.match.params.companyId] },
              { key: 'country_id', values: [props.match.params.countryId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/location/:locationId',
    component: function AssetsByLocation(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              { key: 'location', values: [props.match.params.locationId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/user/:userId/company/:companyId',
    component: function AssetsByAccountAndCompany(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'user', values: [props.match.params.userId] },
              { key: 'company', values: [props.match.params.companyId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/user/:userId/location/:locationId',
    component: function AssetsByLocationAndUser(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'user', values: [props.match.params.userId] },
              { key: 'location', values: [props.match.params.locationId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/user/:userId/contract/:contractId',
    component: function AssetsByContractAndUser(props: *) {
      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'user', values: [props.match.params.userId] },
              { key: 'contract_id', values: [props.match.params.contractId] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/assets/account/:accountId/:assignedTo/status/:status/',
    component: function AssetsByAssignedToAndStatus(props: *) {
      const assignedTo =
        props.match.params.assignedTo === 'locations'
          ? 'has_location'
          : props.match.params.assignedTo === 'projects'
          ? 'has_project'
          : props.match.params.assignedTo === 'users'
          ? 'has_user'
          : null;

      return (
        <Redirect
          to={formulateUri('/assets', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              ...(assignedTo ? [{ key: 'has_relationship', values: [assignedTo] }] : []),
              { key: 'asset_status', values: [props.match.params.status] },
              { key: 'asset_type', values: ['Rented Open', 'Owned'] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
];

export default assetRoutes.map(item => ({ ...item, store: ['assetStore', 'actionDrawerStore'] }));
